import toast from 'react-hot-toast';
import { useAccount, useSignMessage } from 'wagmi';

import { convertValueToWei } from '@/utils/convertValueToWei';
import { encodeAbi, IPayload } from '@/utils/encodeAbi';
import { getContractDecimals } from '@/utils/getContractDecimals';
import { generateUniqueNumber } from '@/utils/helper';
import { convertDayToUnix } from '@/utils/unixTimestamp';
import { useErc20Contract, useNFTContract } from 'smart-contracts/contracts';
import { IDesiredTerms } from 'types/loan';

import { useApproveMiddleware } from './useApproveMiddleware';

interface IMakeLoanRequestParams {
  terms: IDesiredTerms & {
    termUnix?: number;
  };
  tokenId: string;
  collectionAddress: string;
}

const contractsWithoutOwnerFunc = ['0x3E34ff1790BF0a13EfD7d77e75870Cb525687338'];

const useMakeLoanRequest = () => {
  const generateNFTContract = useNFTContract();
  const generateERC20Contract = useErc20Contract();
  const { address } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { approveNFTMiddleware } = useApproveMiddleware();

  const makeLoanRequest = async ({ terms: { apr, principal, term, termUnix: termUnixDefault, currency }, tokenId, collectionAddress }: IMakeLoanRequestParams) => {
    if (!address) return null;
    const NFTContract = generateNFTContract(collectionAddress);

    if (!contractsWithoutOwnerFunc.includes(collectionAddress)) {
      const [tokenOwnerAddress] = await NFTContract.functions.ownerOf(tokenId);

      if (tokenOwnerAddress !== address) {
        throw new Error(`You are not owner of ${tokenId} nft`);
      }
    }
    const ERC20Contract = generateERC20Contract(currency);
    if (!ERC20Contract) return null;
    const decimals = await getContractDecimals(ERC20Contract);

    await approveNFTMiddleware(collectionAddress, tokenId);

    const termUnix = termUnixDefault || convertDayToUnix(term);
    const nonce = generateUniqueNumber();

    const payload: IPayload = {
      tokenId: {
        type: 'uint256[]',
        value: [tokenId],
      },
      nftContractAddress: { type: 'address', value: collectionAddress },
      erc20TokenAddress: { type: 'address', value: currency },
      borrower: { type: 'address', value: address },
      loanAmount: {
        type: 'uint256',
        value: convertValueToWei(principal, decimals),
      },
      aprBasisPoints: {
        type: 'uint256',
        value: apr * 100,
      },
      loanDuration: {
        type: 'uint256',
        value: termUnix,
      },
      nonce: {
        type: 'uint256',
        value: nonce,
      },
    };

    const hash = encodeAbi(payload);
    const signaturePromise = signMessageAsync({
      message: { raw: hash },
    });

    const signature = await toast.promise(signaturePromise, {
      loading: 'Please generate the signature.',
      success: 'The signature has been successfully generated.',
      error: 'Something went wrong. Please check the MetaMask logs.',
    });

    return { signature, termUnix, nonce };
  };

  return { makeLoanRequest };
};

export { useMakeLoanRequest };
